import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSpinner,
  FaClock,
  FaBook,
  FaHeadphones,
  FaPencilAlt,
  FaInfoCircle,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminCreateExamSet: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    setName: "",
    level: "A1",
    variant: 0,
    lesenDuration: 65,
    horenDuration: 30,
    schreibenDuration: 45,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Recommended durations by level
  const recommendedDurations: Record<
    string,
    { lesen: number; horen: number; schreiben: number }
  > = {
    A1: { lesen: 65, horen: 25, schreiben: 20 },
    A2: { lesen: 70, horen: 30, schreiben: 45 },
    B1: { lesen: 65, horen: 40, schreiben: 70 },
    B2: { lesen: 75, horen: 35, schreiben: 100 },
  };

  const handleLevelChange = (level: string) => {
    const recommended = recommendedDurations[level];
    setFormData({
      ...formData,
      level,
      lesenDuration: recommended.lesen,
      horenDuration: recommended.horen,
      schreibenDuration: recommended.schreiben,
    });
  };

  const totalDuration =
    formData.lesenDuration +
    formData.horenDuration +
    formData.schreibenDuration;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/exams`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        const exam = result.data || result;
        // Redirect to manage questions page
        navigate(`/admin/exams/${exam.id}/builder`);
      } else {
        const data = await response.json();
        setError(data.message || data.error || "Failed to create exam set");
      }
    } catch (err) {
      console.error("Error creating exam:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaPlus />}
        title="Create Exam Set"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Create" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Info Card */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            Creating a German Exam Set
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">1.</span>
              <span>
                Give your exam set a descriptive name (e.g., "Goethe A1 Practice
                Test")
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">2.</span>
              <span>
                Select the CEFR level (A1, A2, B1, B2) and variant number
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">3.</span>
              <span>
                Set duration for each module (recommended durations auto-fill by
                level)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">4.</span>
              <span>After creating, you'll add questions for each module</span>
            </li>
          </ul>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Exam Set Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Exam Set Name *
              </label>
              <input
                type="text"
                value={formData.setName}
                onChange={(e) =>
                  setFormData({ ...formData, setName: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                placeholder="e.g., Goethe A1 Practice Test - December 2024"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Choose a descriptive name that helps identify this exam set
              </p>
            </div>

            {/* Level and Variant */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  CEFR Level *
                </label>
                <select
                  value={formData.level}
                  onChange={(e) => handleLevelChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                  style={{ fontSize: "16px" }}
                >
                  <option value="A1">A1 - Beginner</option>
                  <option value="A2">A2 - Elementary</option>
                  <option value="B1">B1 - Intermediate</option>
                  <option value="B2">B2 - Upper Intermediate</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Variant Number *
                </label>
                <input
                  type="number"
                  value={formData.variant}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      variant: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                  placeholder="e.g., 1, 2, 3"
                  style={{ fontSize: "16px" }}
                  min="1"
                  max="99"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter a number (e.g., 1 for Variante 1)
                </p>
              </div>
            </div>

            {/* Module Durations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaClock className="text-[#009fe3]" />
                Module Durations (minutes)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Recommended durations have been set based on the selected level.
                You can adjust them as needed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Lesen */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center">
                      <FaBook size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Lesen</h4>
                      <p className="text-xs text-gray-600">Reading</p>
                    </div>
                  </div>
                  <input
                    type="number"
                    value={formData.lesenDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        lesenDuration: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center font-bold text-lg"
                    style={{ fontSize: "18px" }}
                    min="1"
                    max="300"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Recommended: {recommendedDurations[formData.level].lesen}{" "}
                    min
                  </p>
                </div>

                {/* Hören */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-purple-600 text-white rounded-lg flex items-center justify-center">
                      <FaHeadphones size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Hören</h4>
                      <p className="text-xs text-gray-600">Listening</p>
                    </div>
                  </div>
                  <input
                    type="number"
                    value={formData.horenDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        horenDuration: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-center font-bold text-lg"
                    style={{ fontSize: "18px" }}
                    min="1"
                    max="300"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Recommended: {recommendedDurations[formData.level].horen}{" "}
                    min
                  </p>
                </div>

                {/* Schreiben */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center">
                      <FaPencilAlt size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Schreiben</h4>
                      <p className="text-xs text-gray-600">Writing</p>
                    </div>
                  </div>
                  <input
                    type="number"
                    value={formData.schreibenDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        schreibenDuration: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-center font-bold text-lg"
                    style={{ fontSize: "18px" }}
                    min="1"
                    max="300"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Recommended:{" "}
                    {recommendedDurations[formData.level].schreiben} min
                  </p>
                </div>
              </div>

              {/* Total Duration */}
              <div className="mt-4 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaClock className="text-cyan-600" />
                    <span className="font-semibold text-gray-900">
                      Total Exam Duration:
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-cyan-600">
                    {totalDuration} minutes
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Students will have this total time to complete all three
                  modules
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                <p className="font-semibold">Error</p>
                <p>{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Creating Exam Set...
                  </>
                ) : (
                  <>
                    <FaPlus />
                    Create Exam Set
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/exam-management")}
                disabled={loading}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>

            <p className="text-sm text-gray-600 text-center">
              After creating the exam set, you'll be redirected to add questions
              for each module
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateExamSet;
